import { EVENT } from './eventNames';

/**
 * @typedef {Object} GameAttackTypes
 * @property {number} REGULAR
 * @property {number} NON_RESPONSIVE
 * @property {number} POSITION_ALTERING
 * @property {number} FALSE_INFORMATION
 * @property {number} NON_MOVING
 * @property {number} FALSE_SQUAWK
 * @property {number} FALSE_HEADING
 * @property {number} DUPLICATED_AIRCRAFT
 * @property {number} IMPERSONATED_AIRCRAFT
 * @property {number} SYBIL
 * @property {number} REPLAY_ATTACK
 * @property {number} FLOODING_OF_NON_RESPONSIVE
 */

/**
 * TODO: Move to attackConstants.js
 * Enumeration of available game attacks
 *
 * @type {GameAttackTypes}
 * @final
 */
export const GAME_ATTACK_TYPES = {
    REGULAR: 0,
    NON_RESPONSIVE: 1,
    POSITION_ALTERING: 2,
    FALSE_INFORMATION: 3,
    NON_MOVING: 4,
    FALSE_SQUAWK: 5,
    FALSE_HEADING: 6,
    DUPLICATED_AIRCRAFT: 7,
    IMPERSONATED_AIRCRAFT: 8,
    SYBIL: 9,
    REPLAY_ATTACK: 10,
    FLOODING_OF_NON_RESPONSIVE: 11,
    GPS_JAMMING: 12,
    DRONE_ATTACK: 13
};

/* eslint-disable max-len, import/prefer-default-export */
/**
 * TODO: Move to attackConstants.js
 * Name enumeration of available game attacks
 *
 * @property GAME_ATTACK_NAMES
 * @type {Object}
 * @final
 */
export const GAME_ATTACK_NAMES = {
    0: { name: ['Regular'], color: ['#FFFFFF'] },
    1: { name: ['Non responsive'], color: ['#e6194B'] }, //red
    2: { name: ['Position-altering'], color: ['#911eb4'] }, //purple // position altering doesn't work as of now
    3: { name: ['False information'], color: ['#4363d8'] }, //blue
    4: { name: ['Standing still'], color: ['#808000'] }, //Olive green
    5: { name: ['False squawk'], color: ['#800000'] }, //maroon red
    6: { name: ['False heading'], color: ['#469990'] }, //teal (green ish) // false heading doesn't work as of now
    7: { name: ['Duplicate aircraft'], color: ['#3cb44b','#FFFFFF'] }, // Green, duplicate
    8: { name: ['Impersonate aircraft'], color: ['#ffe119','#FFFFFF'] }, // Yellow, impersonate
    9: { name: ['Sybil'], color: ['#f032e6'] }, //Magenta, sybil // sybil doesn't work as of now
    11: { name: ['Flooding attack'], color: ['#fabed4'] }, //Pink
    12: { name: ['GPS Jamming'], color: ['#d44204'] }, //Orange
    13: { name: ['Drone attack'], color: ['#42d4f4'] }, //Aqua blue
};

export const ATTACK_SETTING_CATEGORY = {
    GENERAL: 'General',
    PROBABILITY_OF_AN_ATTACK: 'Probability of an attack',
    OTHER_ATTACKS: 'Other attacks',
    JUMPING_OPTIONS: 'Jumping options',
    NON_MOVING_OPTIONS: 'Standing still options',
    FALSE_HEADING_OPTIONS: 'False heading options',
    SYBIL_ATTACK_OPTIONS: 'Sybil attack options',
    DRONE_ATTACK_OPTIONS: 'Drone attack options',
    OTHER_ATTACK_OPTIONS: 'Other attack options',
    GPS_JAMMING_OPTIONS: 'GPS Jamming options',
};

export const ATTACK_SETTING_NAMES = {
    /**
     * General settings
     */
    PERCENTAGE_OF_PRESPAWNED_AIRCRAFT: 'amountOfPreSpawnedAircraft',
    PROBABILITY_OF_NEW_AIRCRAFT: 'probabilityOfNewAircraft',
    SHOW_ATTACK_AIRCRAFT_VISIBILITY: 'showAttackAircraftVisibility',
    /**
     * Probability of non-responsive aircraft
     */
    PROBABILITY_OF_NON_RESPONSIVE: 'nonResponsiveProbability',
    /**
     * Flooding of non-responsive aircraft
     */
    FLOODING_OF_NON_RESPONSIVE: 'floodingOfNonResponsive',
    /**
     * Probability of position altering
     * Probability of jumps
     * Distance of jumps
     */
    PROBABILITY_OF_POSITION_ALTERING: 'positionAlterProbability',
    JUMP_PROBABILITY: 'jumpProbability',
    JUMP_RADIUS: 'jumpRadius',
    MAXIMUM_OF_JUMPS: 'maximumOfJumps',
    /**
     * Probability of false information
     */
    PROBABILITY_OF_FALSE_INFORMATION: 'falseInformationProbability',
    /**
     * Probability of Standing still aircraft
     */
    PROBABILITY_OF_NON_MOVING: 'nonMovingProbability',
    NON_MOVING_MIN_TIME: 'nonMovingMinTime',
    NON_MOVING_MAX_TIME: 'nonMovingMaxTime',
    /**
     * Probability of false squawk
     */
    PROBABILITY_OF_FALSE_SQUAWK: 'transponderCodeAlterProbability',
    /**
     * Probability of false heading
     */
    PROBABILITY_OF_FALSE_HEADING: 'virtualTrajectoryAlterProbability',
    FALSE_HEADING_WEIGHT: 'falseHeadingWeight',
    FALSE_HEADING_RATE: 'falseHeadingRate',
    /**
     * Probability of duplicated aircraft
     */
    PROBABILITY_OF_DUPLICATED_AIRCRAFT: 'duplicateProbability',
    /**
     * Probability of impersonated aircraft
     */
    PROBABILITY_OF_IMPERSONATED_AIRCRAFT: 'impersonateProbability',
    /**
     * Probability of drone attack
     */
    PROBABILITY_OF_DRONE_ATTACK: 'droneAttackProbability',
    /**
     * Probability of sybil attack
     */
    PROBABILITY_OF_SYBIL: 'sybilProbability',
    ACTIVE_TIME_FOR_SYBIL: 'activeTimeForSybil',
    MAXIMUM_OF_SYBIL_AIRCRAFT: 'maximumOfSybilAircraft',
    AMOUNT_OF_SYBIL_NODES: 'amountOfSybilNodes',
    MIN_SPAWN_TIME_FOR_SYBIL: 'minSpawnTimeForSybil',
    MAX_SPAWN_TIME_FOR_SYBIL: 'maxSpawnTimeForSybil',
    /**
     * Safe radius for direct injection
     */
    SAFE_RADIUS_FOR_DIRECT_INJECTION: 'safeRadiusForDirectInjection',
    /**
     * Hide the score
     */
    HIDE_SCORE: 'hideScore',

    TOGGLE_GPS_JAM: 'toggleGpsJam',
    SHOW_AIRCRAFT_TRUE_POS: 'showAircraftTruePos',
    TOGGLE_GPS_JAMMING_ZONES_VISIBILITY: 'toggleGpsJammingZonesVisibility',
    GPS_JAMMING_HIGH_NR_ZONES: 'gpsJammingHighNrZones',
    GPS_JAMMING_HIGH_RADIUS: 'gpsJammingHighRadius',
    GPS_JAMMING_LOW_NR_ZONES: 'gpsJammingLowNrZones',
    GPS_JAMMING_LOW_RADIUS: 'gpsJammingLowRadius',

    PROBABILITY_OF_MOVING_DRONE_ATTACK: 'movingDroneAttackProbability',
    DRONE_ATTACK_RADIUS: 'droneAttackRadius',
    DRONE_ATTACK_MIN_DISTANCE_FROM_AIRPORT_CENTER: 'minDistanceAirportCenterDroneAttack',
    DRONE_ATTACK_EXTRA_DESPAWN_TIME: 'droneAttackExtraDespawnTime',
    DRONE_ATTACK_HEADING: 'droneAttackHeading',
    DRONE_ATTACK_MOVING_SPEED: 'droneAttackMovingSpeed',
};

export const ATTACK_SETTING_VERIFIERS = {
    VERIFY_NON_MOVING_MIN_VALUE: 'verifyNonMovingMinValue',
    VERIFY_NON_MOVING_MAX_VALUE: 'verifyNonMovingMaxValue',
    VERIFY_MAX_SPAWN_TIME_FOR_SYBIL: 'verifyMaxSpawnTimeForSybil',
    VERIFY_MIN_SPAWN_TIME_FOR_SYBIL: 'verifyMinSpawnTimeForSybil',
}

export const ATTACK_SETTING_UNITS = {
    PERCENTAGE: '%',
    SECONDS: 's',
    NM: 'nm',
};

/**
 * TODO: Move to attackConstants.js
 * Game attack command whitelist
 *
 * @property GAME_ATTACK_COMMAND_WHITELIST
 * @type {array<string>}
 * @final
 */
export const GAME_ATTACK_COMMAND_WHITELIST = [
    'makeGuess',
    'showType',
    'stopListen',
    'startListen',
    'addText'
];

export const ATTACK_OPTION_VALUES = [
    {
        name: ATTACK_SETTING_NAMES.PERCENTAGE_OF_PRESPAWNED_AIRCRAFT,
        category: ATTACK_SETTING_CATEGORY.GENERAL,
        saveToLocalStorage: true,
        defaultValue: 0,
        description: 'Amount of pre-spawned aircraft affected by attacks',
        type: 'range',
        onChangeEventHandler: null,
        dataType: 'number',
        unit: ATTACK_SETTING_UNITS.PERCENTAGE,
        minValue: 0,
        maxValue: 0.6,
    },
    {
        name: ATTACK_SETTING_NAMES.PROBABILITY_OF_NEW_AIRCRAFT,
        category: ATTACK_SETTING_CATEGORY.GENERAL,
        saveToLocalStorage: true,
        defaultValue: 0,
        description: 'Probability of new aircraft affected by attacks',
        type: 'range',
        onChangeEventHandler: null,
        dataType: 'number',
        unit: ATTACK_SETTING_UNITS.PERCENTAGE,
        minValue: 0,
        maxValue: 1,
    },
    {
        name: ATTACK_SETTING_NAMES.SHOW_ATTACK_AIRCRAFT_VISIBILITY,
        category: ATTACK_SETTING_CATEGORY.GENERAL,
        saveToLocalStorage: true,
        defaultValue: false,
        description: 'Color code attack aircraft',
        type: 'select',
        dataType: 'boolean',
        onChangeEventHandler: null,
        optionList: [
            {
                displayLabel: 'On',
                value: true
            },
            {
                displayLabel: 'Off',
                value: false
            }
        ]
    },
    {
        name: ATTACK_SETTING_NAMES.PROBABILITY_OF_NON_RESPONSIVE,
        category: ATTACK_SETTING_CATEGORY.PROBABILITY_OF_AN_ATTACK,
        saveToLocalStorage: true,
        defaultValue: '0',
        description: 'Aircraft not responding to commands',
        help: 'Simulates spoofed ADS-B data. These aircraft will not respond to any commands.',
        type: 'range',
        dataType: 'number',
        unit: ATTACK_SETTING_UNITS.PERCENTAGE,
        onChangeEventHandler: null,
        minValue: 0,
        maxValue: 1,
    },
    {
        name: ATTACK_SETTING_NAMES.PROBABILITY_OF_POSITION_ALTERING,
        category: ATTACK_SETTING_CATEGORY.PROBABILITY_OF_AN_ATTACK,
        saveToLocalStorage: true,
        defaultValue: '0',
        description: 'Aircraft changing postion',
        help: 'Changes the positional values of ADS-B data of aircraft',
        type: 'range',
        dataType: 'number',
        unit: ATTACK_SETTING_UNITS.PERCENTAGE,
        onChangeEventHandler: null,
        minValue: 0,
        maxValue: 1,
    },
    {
        name: ATTACK_SETTING_NAMES.JUMP_PROBABILITY,
        category: ATTACK_SETTING_CATEGORY.JUMPING_OPTIONS,
        saveToLocalStorage: true,
        defaultValue: '0',
        description: 'Probability of jumps',
        help: 'Probability of jumping aircraft to jump',
        type: 'range',
        dataType: 'number',
        unit: ATTACK_SETTING_UNITS.PERCENTAGE,
        onChangeEventHandler: null,
        minValue: 0,
        maxValue: 1,
    },
    {
        name: ATTACK_SETTING_NAMES.JUMP_RADIUS,
        category: ATTACK_SETTING_CATEGORY.JUMPING_OPTIONS,
        saveToLocalStorage: true,
        defaultValue: 0.5,
        description: 'Distance of jumps',
        help: 'The distance of a positional ADS-B data jump, when it occurs. ',
        type: 'select',
        dataType: 'number',
        onChangeEventHandler: null,
        optionList: [
            {
                displayLabel: 'Small radius',
                value: 0.5
            },
            {
                displayLabel: 'Moderate radius',
                value: 1
            },
            {
                displayLabel: 'Large radius',
                value: 2
            }
        ]
    },
    {
        name: ATTACK_SETTING_NAMES.MAXIMUM_OF_JUMPS,
        category: ATTACK_SETTING_CATEGORY.JUMPING_OPTIONS,
        saveToLocalStorage: true,
        defaultValue: 3,
        description: 'Maximum number of jumps',
        help: 'The maximum number of jumps an aircraft can make',
        type: 'range',
        dataType: 'number',
        onChangeEventHandler: null,
        minValue: 3,
        maxValue: 7,
    },
    {
        name: ATTACK_SETTING_NAMES.PROBABILITY_OF_FALSE_INFORMATION,
        category: ATTACK_SETTING_CATEGORY.PROBABILITY_OF_AN_ATTACK,
        saveToLocalStorage: true,
        defaultValue: '0',
        description: 'Aircraft showing false data',
        help: 'Changes the alitude and velocity values of ADS-B data of aircraft',
        type: 'range',
        dataType: 'number',
        unit: ATTACK_SETTING_UNITS.PERCENTAGE,
        onChangeEventHandler: null,
        minValue: 0,
        maxValue: 1,
    },
    {
        name: ATTACK_SETTING_NAMES.PROBABILITY_OF_NON_MOVING,
        category: ATTACK_SETTING_CATEGORY.PROBABILITY_OF_AN_ATTACK,
        saveToLocalStorage: true,
        defaultValue: '0',
        description: 'Aircraft will stand still',
        help: 'Make aircraft stand still with velocity zero',
        type: 'range',
        dataType: 'number',
        unit: ATTACK_SETTING_UNITS.PERCENTAGE,
        onChangeEventHandler: null,
        minValue: 0,
        maxValue: 1,
    },
    {
        name: ATTACK_SETTING_NAMES.NON_MOVING_MIN_TIME,
        category: ATTACK_SETTING_CATEGORY.NON_MOVING_OPTIONS,
        saveToLocalStorage: true,
        defaultValue: 3,
        description: 'Minimum time in seconds an aircraft will stand still',
        help: 'Minimum time in seconds an aircraft will stand still',
        type: 'range',
        dataType: 'number',
        unit: ATTACK_SETTING_UNITS.SECONDS,
        onChangeEventHandler: null,
        dataVerification: ATTACK_SETTING_VERIFIERS.VERIFY_NON_MOVING_MIN_VALUE,
        minValue: 1,
        maxValue: 60,
    },
    {
        name: ATTACK_SETTING_NAMES.NON_MOVING_MAX_TIME,
        category: ATTACK_SETTING_CATEGORY.NON_MOVING_OPTIONS,
        saveToLocalStorage: true,
        defaultValue: 12,
        description: 'Maximum time in seconds an aircraft will stand still',
        help: 'Maximum time in seconds an aircraft will stand still',
        type: 'range',
        dataType: 'number',
        unit: ATTACK_SETTING_UNITS.SECONDS,
        onChangeEventHandler: null,
        dataVerification: ATTACK_SETTING_VERIFIERS.VERIFY_NON_MOVING_MAX_VALUE,
        minValue: 1,
        maxValue: 60,
    },
    {
        name: ATTACK_SETTING_NAMES.PROBABILITY_OF_FALSE_SQUAWK,
        category: ATTACK_SETTING_CATEGORY.PROBABILITY_OF_AN_ATTACK,
        saveToLocalStorage: true,
        defaultValue: '0',
        description: 'Aircraft changing squawk value',
        help: 'Changes the squawk code to emergency codes and / or invalid values.',
        type: 'range',
        dataType: 'number',
        unit: ATTACK_SETTING_UNITS.PERCENTAGE,
        onChangeEventHandler: null,
        minValue: 0,
        maxValue: 1,
    },
    {
        name: ATTACK_SETTING_NAMES.PROBABILITY_OF_FALSE_HEADING,
        category: ATTACK_SETTING_CATEGORY.PROBABILITY_OF_AN_ATTACK,
        saveToLocalStorage: true,
        defaultValue: '0',
        description: 'Aircraft changing heading value',
        help: 'Changes the heading of the aircraft',
        type: 'range',
        dataType: 'number',
        unit: ATTACK_SETTING_UNITS.PERCENTAGE,
        onChangeEventHandler: null,
        minValue: 0,
        maxValue: 1,
    },
    {
        name: ATTACK_SETTING_NAMES.FALSE_HEADING_WEIGHT,
        category: ATTACK_SETTING_CATEGORY.FALSE_HEADING_OPTIONS,
        saveToLocalStorage: true,
        defaultValue: 3,
        description: 'The weight of heading change',
        help: 'The weight of heading change',
        type: 'range',
        dataType: 'number',
        onChangeEventHandler: null,
        minValue: 1,
        maxValue: 10,
    },
    {
        name: ATTACK_SETTING_NAMES.FALSE_HEADING_RATE,
        category: ATTACK_SETTING_CATEGORY.FALSE_HEADING_OPTIONS,
        saveToLocalStorage: true,
        defaultValue: 12,
        description: 'The number of seconds between heading changes',
        help: 'The number of seconds between heading changes',
        type: 'range',
        dataType: 'number',
        unit: ATTACK_SETTING_UNITS.SECONDS,
        onChangeEventHandler: null,
        minValue: 1,
        maxValue: 60,
    },
    {
        name: ATTACK_SETTING_NAMES.PROBABILITY_OF_DUPLICATED_AIRCRAFT,
        category: ATTACK_SETTING_CATEGORY.PROBABILITY_OF_AN_ATTACK,
        saveToLocalStorage: true,
        defaultValue: '0',
        description: 'Duplicate aircraft',
        help: 'Duplicate an already existing aircraft.',
        type: 'range',
        dataType: 'number',
        unit: ATTACK_SETTING_UNITS.PERCENTAGE,
        onChangeEventHandler: null,
        minValue: 0,
        maxValue: 1,
    },
    {
        name: ATTACK_SETTING_NAMES.PROBABILITY_OF_IMPERSONATED_AIRCRAFT,
        category: ATTACK_SETTING_CATEGORY.PROBABILITY_OF_AN_ATTACK,
        saveToLocalStorage: true,
        defaultValue: '0',
        description: 'Impersonate aircraft',
        help: 'Impersonate an already existing aircraft.',
        type: 'range',
        dataType: 'number',
        unit: ATTACK_SETTING_UNITS.PERCENTAGE,
        onChangeEventHandler: null,
        minValue: 0,
        maxValue: 1,
    },
    {
        name: ATTACK_SETTING_NAMES.PROBABILITY_OF_SYBIL,
        category: ATTACK_SETTING_CATEGORY.PROBABILITY_OF_AN_ATTACK,
        saveToLocalStorage: true,
        defaultValue: '0',
        description: 'Sybil attack',
        help: 'A swarm of aircrafts with stolen IDs will spawn around a random aircraft',
        type: 'range',
        dataType: 'number',
        unit: ATTACK_SETTING_UNITS.PERCENTAGE,
        onChangeEventHandler: null,
        minValue: 0,
        maxValue: 1,
    },
    {
        name: ATTACK_SETTING_NAMES.PROBABILITY_OF_DRONE_ATTACK,
        category: ATTACK_SETTING_CATEGORY.PROBABILITY_OF_AN_ATTACK,
        saveToLocalStorage: true,
        defaultValue: '0',
        description: 'Intruding drone attack',
        help: 'Intruding drone enters airspace',
        type: 'range',
        dataType: 'number',
        unit: ATTACK_SETTING_UNITS.PERCENTAGE,
        onChangeEventHandler: null,
        minValue: 0,
        maxValue: 1,
    },
    {
        name: ATTACK_SETTING_NAMES.MAXIMUM_OF_SYBIL_AIRCRAFT,
        category: ATTACK_SETTING_CATEGORY.SYBIL_ATTACK_OPTIONS,
        saveToLocalStorage: true,
        defaultValue: 3,
        description: 'Maximum amount of main sybils that can spawn',
        help: 'How many main sybil nodes that can spawn with the spawn scheduler',
        type: 'range',
        dataType: 'number',
        onChangeEventHandler: null,
        minValue: 1,
        maxValue: 7,
    },
    {
        name: ATTACK_SETTING_NAMES.AMOUNT_OF_SYBIL_NODES,
        category: ATTACK_SETTING_CATEGORY.SYBIL_ATTACK_OPTIONS,
        saveToLocalStorage: true,
        defaultValue: 5,
        description: 'Amount of sybil nodes',
        help: 'How many sybil nodes that will spawn',
        type: 'range',
        dataType: 'number',
        onChangeEventHandler: null,
        minValue: 1,
        maxValue: 7,
    },
    {
        name: ATTACK_SETTING_NAMES.ACTIVE_TIME_FOR_SYBIL,
        category: ATTACK_SETTING_CATEGORY.SYBIL_ATTACK_OPTIONS,
        saveToLocalStorage: true,
        defaultValue: 60,
        description: 'Active time for sybil nodes',
        help: 'How long the sybil nodes will be active',
        type: 'range',
        dataType: 'number',
        unit: ATTACK_SETTING_UNITS.SECONDS,
        onChangeEventHandler: null,
        minValue: 10,
        maxValue: 300,
    },
    {
        name: ATTACK_SETTING_NAMES.MIN_SPAWN_TIME_FOR_SYBIL,
        category: ATTACK_SETTING_CATEGORY.SYBIL_ATTACK_OPTIONS,
        saveToLocalStorage: true,
        defaultValue: 10,
        description: 'Minimum time between the nodes are spawned',
        help: 'Minimum time between the nodes are spawned',
        type: 'range',
        dataType: 'number',
        unit: ATTACK_SETTING_UNITS.SECONDS,
        onChangeEventHandler: null,
        dataVerification: ATTACK_SETTING_VERIFIERS.VERIFY_MIN_SPAWN_TIME_FOR_SYBIL,
        minValue: 10,
        maxValue: 120,
    },
    {
        name: ATTACK_SETTING_NAMES.MAX_SPAWN_TIME_FOR_SYBIL,
        category: ATTACK_SETTING_CATEGORY.SYBIL_ATTACK_OPTIONS,
        saveToLocalStorage: true,
        defaultValue: 20,
        description: 'Maximum time between the nodes are spawned',
        help: 'Maximum time between the nodes are spawned',
        type: 'range',
        dataType: 'number',
        unit: ATTACK_SETTING_UNITS.SECONDS,
        onChangeEventHandler: null,
        dataVerification: ATTACK_SETTING_VERIFIERS.VERIFY_MAX_SPAWN_TIME_FOR_SYBIL,
        minValue: 10,
        maxValue: 120,
    },
    {
        name: ATTACK_SETTING_NAMES.PROBABILITY_OF_MOVING_DRONE_ATTACK,
        category: ATTACK_SETTING_CATEGORY.DRONE_ATTACK_OPTIONS,
        saveToLocalStorage: true,
        defaultValue: 0.5,
        description: 'Probability that drone attack is moving',
        type: 'range',
        dataType: 'number',
        unit: ATTACK_SETTING_UNITS.PERCENTAGE,
        onChangeEventHandler: null,
        minValue: 0,
        maxValue: 1,
    },
    {
        name: ATTACK_SETTING_NAMES.DRONE_ATTACK_RADIUS,
        category: ATTACK_SETTING_CATEGORY.DRONE_ATTACK_OPTIONS,
        saveToLocalStorage: true,
        defaultValue: 6,
        description: 'Drone attack zone radius',
        type: 'range',
        dataType: 'number',
        onChangeEventHandler: null,
        minValue: 1,
        maxValue: 30,
    },
    {
        name: ATTACK_SETTING_NAMES.DRONE_ATTACK_MIN_DISTANCE_FROM_AIRPORT_CENTER,
        category: ATTACK_SETTING_CATEGORY.DRONE_ATTACK_OPTIONS,
        saveToLocalStorage: true,
        defaultValue: 50,
        description: 'Minimum distance from airport center',
        type: 'range',
        dataType: 'number',
        onChangeEventHandler: null,
        minValue: 0,
        maxValue: 100,
    },
    {
        name: ATTACK_SETTING_NAMES.DRONE_ATTACK_EXTRA_DESPAWN_TIME,
        category: ATTACK_SETTING_CATEGORY.DRONE_ATTACK_OPTIONS,
        saveToLocalStorage: true,
        defaultValue: 30,
        description: 'Drone attack extra despawn time',
        type: 'range',
        dataType: 'number',
        unit: ATTACK_SETTING_UNITS.SECONDS,
        onChangeEventHandler: null,
        minValue: 0,
        maxValue: 200,
    },
    {
        name: ATTACK_SETTING_NAMES.DRONE_ATTACK_HEADING,
        category: ATTACK_SETTING_CATEGORY.DRONE_ATTACK_OPTIONS,
        saveToLocalStorage: true,
        defaultValue: 1,
        description: 'Moving drone attack heading',
        type: 'select',
        onChangeEventHandler: null,
        dataType: 'number',
        optionList: [
            {
                displayLabel: 'North-West',
                value: 1
            },
            {
                displayLabel: 'North-East',
                value: 2
            },
            {
                displayLabel: 'South-West',
                value: 3
            },
            {
                displayLabel: 'South-East',
                value: 4
            },
            {
                displayLabel: 'Random',
                value: 0
            },
        ]
    },
    {
        name: ATTACK_SETTING_NAMES.DRONE_ATTACK_MOVING_SPEED,
        category: ATTACK_SETTING_CATEGORY.DRONE_ATTACK_OPTIONS,
        saveToLocalStorage: true,
        defaultValue: 2,
        description: 'Drone attack moving speed',
        type: 'range',
        dataType: 'number',
        onChangeEventHandler: null,
        minValue: 1,
        maxValue: 3,
    },
    {
        name: ATTACK_SETTING_NAMES.TOGGLE_GPS_JAM,
        category: ATTACK_SETTING_CATEGORY.GPS_JAMMING_OPTIONS,
        saveToLocalStorage: false,
        defaultValue: false,
        description: 'Toggle GPS Jamming attack',
        help: 'Toggle GPS Jamming attack',
        type: 'select',
        dataType: 'boolean',
        onChangeEventHandler: EVENT.TOGGLE_GPS_JAM,
        optionList: [
            {
                displayLabel: 'Yes',
                value: true
            },
            {
                displayLabel: 'No',
                value: false
            }
        ]
    },
    {
        name: ATTACK_SETTING_NAMES.GPS_JAMMING_HIGH_NR_ZONES,
        category: ATTACK_SETTING_CATEGORY.GPS_JAMMING_OPTIONS,
        saveToLocalStorage: true,
        defaultValue: 1,
        description: 'Number of GPS jamming zones with high power',
        help: 'The number of GPS jamming zones with high power that will be created',
        type: 'range',
        dataType: 'number',
        onChangeEventHandler: null,
        minValue: 0,
        maxValue: 5,
    },
    {
        name: ATTACK_SETTING_NAMES.GPS_JAMMING_HIGH_RADIUS,
        category: ATTACK_SETTING_CATEGORY.GPS_JAMMING_OPTIONS,
        saveToLocalStorage: true,
        defaultValue: 50,
        description: 'Radius of GPS jamming zones with high power',
        help: 'The radius of GPS jamming zones with high power that will be created',
        type: 'range',
        dataType: 'number',
        unit: ATTACK_SETTING_UNITS.NM,
        onChangeEventHandler: null,
        minValue: 25,
        maxValue: 100,
    },
    {
        name: ATTACK_SETTING_NAMES.GPS_JAMMING_LOW_NR_ZONES,
        category: ATTACK_SETTING_CATEGORY.GPS_JAMMING_OPTIONS,
        saveToLocalStorage: true,
        defaultValue: 2,
        description: 'Number of GPS jamming zones with low power',
        help: 'The number of GPS jamming zones with low power that will be created',
        type: 'range',
        dataType: 'number',
        onChangeEventHandler: null,
        minValue: 0,
        maxValue: 5,
    },
    {
        name: ATTACK_SETTING_NAMES.GPS_JAMMING_LOW_RADIUS,
        category: ATTACK_SETTING_CATEGORY.GPS_JAMMING_OPTIONS,
        saveToLocalStorage: true,
        defaultValue: 25,
        description: 'Radius of GPS jamming zones with low power',
        help: 'The radius of GPS jamming zones with low power that will be created',
        type: 'range',
        dataType: 'number',
        unit: ATTACK_SETTING_UNITS.NM,
        onChangeEventHandler: null,
        minValue: 10,
        maxValue: 75,
    },
    {
        name: ATTACK_SETTING_NAMES.TOGGLE_GPS_JAMMING_ZONES_VISIBILITY,
        category: ATTACK_SETTING_CATEGORY.GPS_JAMMING_OPTIONS,
        saveToLocalStorage: false,
        defaultValue: false,
        description: 'Toggle visibility of GPS Jamming zones',
        help: 'Toggle visibility of GPS Jamming zones',
        type: 'select',
        dataType: 'boolean',
        onChangeEventHandler: EVENT.TOGGLE_GPS_JAMMING_ZONES_VISIBILITY,
        optionList: [
            {
                displayLabel: 'Yes',
                value: true
            },
            {
                displayLabel: 'No',
                value: false
            }
        ]
    },
    {
        name: ATTACK_SETTING_NAMES.SHOW_AIRCRAFT_TRUE_POS,
        category: ATTACK_SETTING_CATEGORY.GPS_JAMMING_OPTIONS,
        saveToLocalStorage: false,
        defaultValue: false,
        description: 'Show aircraft true position',
        help: 'Show aircraft true position (helpful during GPS jamming attack)',
        type: 'select',
        dataType: 'boolean',
        onChangeEventHandler: EVENT.SHOW_AIRCRAFT_TRUE_POS,
        optionList: [
            {
                displayLabel: 'Yes',
                value: true
            },
            {
                displayLabel: 'No',
                value: false
            }
        ]
    },
    {
        name: ATTACK_SETTING_NAMES.FLOODING_OF_NON_RESPONSIVE,
        category: ATTACK_SETTING_CATEGORY.OTHER_ATTACKS,
        saveToLocalStorage: false,
        defaultValue: 0,
        description: 'Flooding of non-responsive aircraft',
        help: 'Creates a new aircraft and gives them the non-responsive tag.',
        type: 'select',
        onChangeEventHandler: EVENT.SET_FLOODING_NON_RESPONSIVE,
        dataType: 'number',
        optionList: [
            {
                displayLabel: 'None',
                value: 0
            },
            {
                displayLabel: 'Very low',
                value: 5
            },
            {
                displayLabel: 'Low',
                value: 20
            },
            {
                displayLabel: 'Medium',
                value: 40
            },
            {
                displayLabel: 'High',
                value: 60
            },
            {
                displayLabel: 'Very high',
                value: 80
            },
            {
                displayLabel: 'Crash computer',
                value: 100
            },
        ]
    },
    {
        name: ATTACK_SETTING_NAMES.SAFE_RADIUS_FOR_DIRECT_INJECTION,
        category: ATTACK_SETTING_CATEGORY.OTHER_ATTACK_OPTIONS,
        saveToLocalStorage: true,
        defaultValue: 8,
        description: 'Safe radius for direct injection',
        help: 'The safe radius for direct injection',
        type: 'range',
        dataType: 'number',
        unit: ATTACK_SETTING_UNITS.NM,
        onChangeEventHandler: null,
        minValue: 5,
        maxValue: 50,
    },
    {
        name: ATTACK_SETTING_NAMES.HIDE_SCORE,
        category: ATTACK_SETTING_CATEGORY.OTHER_ATTACK_OPTIONS,
        saveToLocalStorage: true,
        defaultValue: false,
        description: 'Hide the score',
        help: 'Hide the score',
        type: 'select',
        dataType: 'boolean',
        onChangeEventHandler: EVENT.TOGGLE_SCORE_DISPLAY,
        optionList: [
            {
                displayLabel: 'Yes',
                value: true
            },
            {
                displayLabel: 'No',
                value: false
            }
        ]
    }
];
